import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Users, 
  Clock, 
  Calendar,
  Lock,
  Crown,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Trophy,
  PieChart,
  Activity
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart as RechartsPie,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

// Colors for charts
const COLORS = ['#0055BB', '#008A55', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export default function StatisticheAvanzate() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [etaDistribution, setEtaDistribution] = useState(null);
  const [eventiPerformance, setEventiPerformance] = useState(null);

  const userPlan = user?.plan || 'free';
  // Pro, Enterprise, and Convenzioni plans can access advanced statistics
  const canAccess = ['pro', 'enterprise', 'convenzioni'].includes(userPlan);

  useEffect(() => {
    if (canAccess) {
      fetchAllData();
    } else {
      setLoading(false);
    }
  }, [anno, canAccess]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchOverview(),
        fetchTrend(),
        fetchEtaDistribution(),
        fetchEventiPerformance()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverview = async () => {
    try {
      const res = await fetch(`${API}/api/statistiche-avanzate/overview?anno=${anno}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setOverview(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchTrend = async () => {
    try {
      const res = await fetch(`${API}/api/statistiche-avanzate/trend-ore?anni=3`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTrend(data.trend);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchEtaDistribution = async () => {
    try {
      const res = await fetch(`${API}/api/statistiche-avanzate/distribuzione-eta`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEtaDistribution(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchEventiPerformance = async () => {
    try {
      const res = await fetch(`${API}/api/statistiche-avanzate/performance-eventi?anno=${anno}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEventiPerformance(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const renderTrend = (value) => {
    if (value > 0) {
      return <span className="text-green-600 flex items-center gap-1"><ArrowUpRight className="w-4 h-4" />+{value}%</span>;
    } else if (value < 0) {
      return <span className="text-red-600 flex items-center gap-1"><ArrowDownRight className="w-4 h-4" />{value}%</span>;
    }
    return <span className="text-slate-500 flex items-center gap-1"><Minus className="w-4 h-4" />0%</span>;
  };

  if (!canAccess) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Statistiche Avanzate</h1>
          <p className="text-slate-500 mt-1">Analisi dettagliate e report comparativi</p>
        </div>

        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 bg-purple-100 rounded-full">
                <Lock className="w-8 h-8 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-800">Funzionalità PRO</h3>
                <p className="text-purple-700 mt-1">
                  Le statistiche avanzate con confronti anno su anno, distribuzioni e trend sono disponibili esclusivamente per il piano PRO.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center mt-2">
                <Badge variant="outline" className="text-purple-600">Confronti YoY</Badge>
                <Badge variant="outline" className="text-purple-600">Trend multi-anno</Badge>
                <Badge variant="outline" className="text-purple-600">Distribuzione età</Badge>
                <Badge variant="outline" className="text-purple-600">Performance eventi</Badge>
              </div>
              <Button 
                onClick={() => window.location.href = '/pricing'}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Crown className="w-4 h-4 mr-2" />
                Passa al Piano PRO
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Statistiche Avanzate</h1>
          <p className="text-slate-500 mt-1">Analisi dettagliate e report comparativi</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={anno.toString()} onValueChange={(v) => setAnno(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="select-anno">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...Array(5)].map((_, i) => {
                const y = new Date().getFullYear() - i;
                return <SelectItem key={y} value={y.toString()}>{y}</SelectItem>;
              })}
            </SelectContent>
          </Select>
          <Badge className="bg-purple-600">PRO</Badge>
        </div>
      </div>

      {overview && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Volontari Attivi</p>
                    <p className="text-3xl font-bold text-slate-900">{overview.volontari.attivi}</p>
                    <div className="text-sm mt-1">
                      {renderTrend(overview.volontari.crescita_percentuale)}
                      <span className="text-slate-400 ml-1">vs {overview.anno_precedente}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Ore Volontariato {anno}</p>
                    <p className="text-3xl font-bold text-slate-900">{overview.ore_volontariato.totali_anno_corrente}</p>
                    <div className="text-sm mt-1">
                      {renderTrend(overview.ore_volontariato.crescita_percentuale)}
                      <span className="text-slate-400 ml-1">vs {overview.anno_precedente}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <Clock className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Eventi {anno}</p>
                    <p className="text-3xl font-bold text-slate-900">{overview.eventi.totali_anno_corrente}</p>
                    <div className="text-sm mt-1">
                      {renderTrend(overview.eventi.crescita_percentuale)}
                      <span className="text-slate-400 ml-1">vs {overview.anno_precedente}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-amber-100 rounded-full">
                    <Calendar className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Media Ore/Volontario</p>
                    <p className="text-3xl font-bold text-slate-900">{overview.ore_volontariato.media_per_volontario}</p>
                    <p className="text-sm text-slate-400 mt-1">
                      su {overview.ore_volontariato.volontari_con_presenze} volontari
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-full">
                    <Activity className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <Tabs defaultValue="andamento" className="space-y-4">
            <TabsList>
              <TabsTrigger value="andamento" data-testid="tab-andamento">
                <BarChart3 className="w-4 h-4 mr-2" />
                Andamento Mensile
              </TabsTrigger>
              <TabsTrigger value="trend" data-testid="tab-trend">
                <TrendingUp className="w-4 h-4 mr-2" />
                Trend Multi-Anno
              </TabsTrigger>
              <TabsTrigger value="distribuzione" data-testid="tab-distribuzione">
                <PieChart className="w-4 h-4 mr-2" />
                Distribuzione Età
              </TabsTrigger>
              <TabsTrigger value="top" data-testid="tab-top">
                <Trophy className="w-4 h-4 mr-2" />
                Top Volontari
              </TabsTrigger>
            </TabsList>

            {/* ANDAMENTO MENSILE */}
            <TabsContent value="andamento">
              <Card>
                <CardHeader>
                  <CardTitle>Andamento Mensile {anno}</CardTitle>
                  <CardDescription>Ore volontariato e partecipazione per mese</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={overview.andamento_mensile}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="mese_nome" />
                        <YAxis yAxisId="left" orientation="left" stroke="#0055BB" />
                        <YAxis yAxisId="right" orientation="right" stroke="#008A55" />
                        <Tooltip />
                        <Legend />
                        <Bar yAxisId="left" dataKey="ore" fill="#0055BB" name="Ore" radius={[4, 4, 0, 0]} />
                        <Bar yAxisId="right" dataKey="volontari_attivi" fill="#008A55" name="Volontari Attivi" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Monthly Data Table */}
                  <div className="mt-6 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Mese</TableHead>
                          <TableHead className="text-right">Ore</TableHead>
                          <TableHead className="text-right">Volontari</TableHead>
                          <TableHead className="text-right">Eventi</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {overview.andamento_mensile.map((m) => (
                          <TableRow key={m.mese}>
                            <TableCell className="font-medium">{m.mese_nome}</TableCell>
                            <TableCell className="text-right">{m.ore}</TableCell>
                            <TableCell className="text-right">{m.volontari_attivi}</TableCell>
                            <TableCell className="text-right">{m.eventi}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* TREND MULTI-ANNO */}
            <TabsContent value="trend">
              <Card>
                <CardHeader>
                  <CardTitle>Trend Ore Volontariato</CardTitle>
                  <CardDescription>Andamento negli ultimi 3 anni</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={trend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="anno" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Area 
                          type="monotone" 
                          dataKey="ore_totali" 
                          stroke="#0055BB" 
                          fill="#0055BB" 
                          fillOpacity={0.3}
                          name="Ore Totali" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  {/* YoY Comparison */}
                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Anno Corrente vs Precedente</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-2xl font-bold">{overview.ore_volontariato.totali_anno_corrente}</span>
                        <span className="text-slate-400">vs</span>
                        <span className="text-xl text-slate-600">{overview.ore_volontariato.totali_anno_precedente}</span>
                      </div>
                      <div className="mt-2">
                        {renderTrend(overview.ore_volontariato.crescita_percentuale)}
                      </div>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Nuovi Volontari YoY</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-2xl font-bold">{overview.volontari.nuovi_anno_corrente}</span>
                        <span className="text-slate-400">vs</span>
                        <span className="text-xl text-slate-600">{overview.volontari.nuovi_anno_precedente}</span>
                      </div>
                      <div className="mt-2">
                        {renderTrend(overview.volontari.crescita_percentuale)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* DISTRIBUZIONE ETÀ */}
            <TabsContent value="distribuzione">
              <Card>
                <CardHeader>
                  <CardTitle>Distribuzione per Fascia d'Età</CardTitle>
                  <CardDescription>Composizione anagrafica dei volontari attivi</CardDescription>
                </CardHeader>
                <CardContent>
                  {etaDistribution && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPie>
                            <Pie
                              data={etaDistribution.distribuzione}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ fascia, percentuale }) => `${fascia}: ${percentuale}%`}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="count"
                            >
                              {etaDistribution.distribuzione.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </RechartsPie>
                        </ResponsiveContainer>
                      </div>

                      <div className="space-y-3">
                        {etaDistribution.distribuzione.map((d, idx) => (
                          <div key={d.fascia} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx] }} />
                                {d.fascia} anni
                              </span>
                              <span className="font-medium">{d.count} ({d.percentuale}%)</span>
                            </div>
                            <Progress value={d.percentuale} className="h-2" />
                          </div>
                        ))}
                        <div className="pt-4 border-t mt-4">
                          <p className="text-sm text-slate-500">
                            Totale analizzati: <strong>{etaDistribution.totale_analizzati}</strong>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* TOP VOLONTARI */}
            <TabsContent value="top">
              <Card>
                <CardHeader>
                  <CardTitle>Top 10 Volontari {anno}</CardTitle>
                  <CardDescription>Classifica per ore di volontariato</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">#</TableHead>
                        <TableHead>Volontario</TableHead>
                        <TableHead className="text-right">Ore</TableHead>
                        <TableHead className="w-40">Progress</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {overview.top_volontari.map((v, idx) => {
                        const maxOre = overview.top_volontari[0]?.ore || 1;
                        const percent = (v.ore / maxOre) * 100;
                        return (
                          <TableRow key={v.volontario_id}>
                            <TableCell>
                              {idx === 0 ? (
                                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                                  <Trophy className="w-4 h-4 text-amber-600" />
                                </div>
                              ) : idx === 1 ? (
                                <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                                  <span className="font-bold text-slate-600">2</span>
                                </div>
                              ) : idx === 2 ? (
                                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                                  <span className="font-bold text-orange-600">3</span>
                                </div>
                              ) : (
                                <span className="text-slate-500 pl-2">{idx + 1}</span>
                              )}
                            </TableCell>
                            <TableCell className="font-medium">{v.nome}</TableCell>
                            <TableCell className="text-right font-semibold text-blue-600">{v.ore} h</TableCell>
                            <TableCell>
                              <Progress value={percent} className="h-2" />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Eventi Performance */}
          {eventiPerformance && eventiPerformance.eventi.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Performance Eventi {anno}</CardTitle>
                <CardDescription>
                  Media partecipanti: {eventiPerformance.statistiche_aggregate.partecipanti_medi} | 
                  Media ore/evento: {eventiPerformance.statistiche_aggregate.ore_medie_per_evento}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Evento</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Stato</TableHead>
                        <TableHead className="text-right">Partecipanti</TableHead>
                        <TableHead className="text-right">Ore Totali</TableHead>
                        <TableHead className="text-right">Media Ore</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {eventiPerformance.eventi.slice(0, 10).map((e) => (
                        <TableRow key={e.evento_id}>
                          <TableCell className="font-medium">{e.titolo}</TableCell>
                          <TableCell>{e.data}</TableCell>
                          <TableCell>
                            <Badge variant={e.stato === 'concluso' ? 'default' : 'secondary'}>
                              {e.stato}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">{e.partecipanti}</TableCell>
                          <TableCell className="text-right">{e.ore_totali}</TableCell>
                          <TableCell className="text-right">{e.ore_media_per_partecipante}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
